import { useEffect, useState } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Icon } from '../components/Icon.tsx';
import { requestsApi, type RequestItemInput } from '../features/requests/api.ts';
import { catalogApi } from '../features/catalog/api.ts';
import { t } from '../lib/strings.ts';

interface ItemRow extends RequestItemInput {
  _key: number;
}

let keyCounter = 0;
function makeRow(partial: Partial<RequestItemInput> = {}): ItemRow {
  return { _key: ++keyCounter, name: '', qty: 1, unit: '', spec: '', ...partial };
}

export function RequestNew(): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [title, setTitle] = useState('');
  const [items, setItems] = useState<ItemRow[]>([makeRow()]);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isPending, setIsPending] = useState(false);

  // Catalog suggestions via datalist
  const [catalogSearch, setCatalogSearch] = useState('');
  const [catalogItems, setCatalogItems] = useState<string[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (!catalogSearch.trim()) return;
      catalogApi.list({ search: catalogSearch.trim() })
        .then((res) => setCatalogItems(res.map((i) => i.name)))
        .catch(() => undefined);
    }, 200);
    return () => clearTimeout(timer);
  }, [catalogSearch]);

  function addRow() {
    setItems((rows) => [...rows, makeRow()]);
  }

  function removeRow(key: number) {
    setItems((rows) => rows.filter((r) => r._key !== key));
  }

  function updateRow(key: number, patch: Partial<RequestItemInput>) {
    setItems((rows) => rows.map((r) => r._key === key ? { ...r, ...patch } : r));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setSubmitError(null);

    const validItems = items.filter((i) => i.name.trim() && i.unit.trim() && i.qty > 0);
    if (validItems.length === 0) {
      setSubmitError(t.requestNew.errorNoItems);
      return;
    }

    setIsPending(true);
    try {
      const created = await requestsApi.create({
        title: title.trim(),
        items: validItems.map(({ name, qty, unit, spec }) => ({
          name: name.trim(),
          qty,
          unit: unit.trim(),
          spec: spec?.trim() || undefined,
        })),
      });
      void qc.invalidateQueries({ queryKey: ['requests'] });
      void navigate({ to: '/requests/$requestId', params: { requestId: created.id } });
    } catch {
      setSubmitError(t.requestNew.errorFailed);
    } finally {
      setIsPending(false);
    }
  }

  return (
    <div className="page space-y-6">
      <header>
        <h1 className="text-2xl font-bold text-ink">{t.requestNew.title}</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {t.requestNew.subtitle}
        </p>
      </header>

      <form className="space-y-6" onSubmit={(e) => { void handleSubmit(e); }}>
        {/* Title */}
        <div className="space-y-1">
          <label className="field-label">{t.requestNew.requestTitle} <span className="text-destructive">*</span></label>
          <input
            required
            className="input"
            placeholder={t.requestNew.requestTitlePlaceholder}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
        </div>

        {/* Items editor */}
        <section className="space-y-2">
          <div className="flex items-baseline justify-between">
            <h2 className="text-base font-semibold text-ink">{t.requestNew.itemsSectionTitle}</h2>
            <span className="text-xs text-muted-foreground num">{items.length}</span>
          </div>

          {/* Catalog datalist */}
          <datalist id="catalog-items">
            {catalogItems.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          <div className="card-paper overflow-hidden">
            {/* Column headers (desktop) — RTL: use text-start so columns flip correctly */}
            <div className="hidden grid-cols-[1fr_5rem_7rem_1fr_2.5rem] gap-3 border-b border-border bg-muted/40 px-4 py-2.5 sm:grid">
              <span className="status-label text-muted-foreground text-start">{t.requestNew.colItem}</span>
              <span className="status-label text-muted-foreground text-start">{t.requestNew.colQty}</span>
              <span className="status-label text-muted-foreground text-start">{t.requestNew.colUnit}</span>
              <span className="status-label text-muted-foreground text-start">{t.requestNew.colSpec}</span>
              <span aria-hidden />
            </div>

            <div className="divide-y divide-border">
              {items.map((row) => (
                <div
                  key={row._key}
                  className="grid grid-cols-1 items-center gap-2 p-3 sm:grid-cols-[1fr_5rem_7rem_1fr_2.5rem] sm:gap-3 sm:px-4"
                >
                  <input
                    required
                    list="catalog-items"
                    className="input"
                    placeholder={t.requestNew.placeholderItem}
                    aria-label={t.requestNew.ariaItem}
                    value={row.name}
                    onChange={(e) => {
                      updateRow(row._key, { name: e.target.value });
                      setCatalogSearch(e.target.value);
                    }}
                  />
                  <input
                    required
                    type="number"
                    min={1}
                    className="input num"
                    placeholder={t.requestNew.placeholderQty}
                    aria-label={t.requestNew.ariaQty}
                    value={row.qty}
                    onChange={(e) => updateRow(row._key, { qty: Number(e.target.value) })}
                  />
                  <input
                    required
                    className="input"
                    placeholder={t.requestNew.placeholderUnit}
                    aria-label={t.requestNew.ariaUnit}
                    value={row.unit}
                    onChange={(e) => updateRow(row._key, { unit: e.target.value })}
                  />
                  <input
                    className="input"
                    placeholder={t.requestNew.placeholderSpec}
                    aria-label={t.requestNew.ariaSpec}
                    value={row.spec}
                    onChange={(e) => updateRow(row._key, { spec: e.target.value })}
                  />
                  <button
                    type="button"
                    onClick={() => removeRow(row._key)}
                    disabled={items.length === 1}
                    className="flex h-9 w-9 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive disabled:pointer-events-none disabled:opacity-30"
                    aria-label={t.requestNew.ariaRemove}
                  >
                    <Icon name="delete" size={15} />
                  </button>
                </div>
              ))}
            </div>

            {/* Add item row */}
            <button
              type="button"
              onClick={addRow}
              className="flex w-full items-center justify-center gap-1.5 border-t border-dashed border-border py-3 text-sm font-medium text-muted-foreground transition-colors hover:bg-muted/40 hover:text-primary"
            >
              <Icon name="add" size={15} /> {t.requestNew.addItem}
            </button>
          </div>
        </section>

        {submitError && (
          <p className="text-sm text-destructive" role="alert">{submitError}</p>
        )}

        <div className="flex gap-2">
          <button type="submit" disabled={isPending} className="btn-primary">
            {isPending ? t.requestNew.submitting : t.requestNew.submit}
          </button>
          <button
            type="button"
            onClick={() => void navigate({ to: '/requests' })}
            className="btn-ghost"
          >
            {t.requestNew.cancel}
          </button>
        </div>
      </form>
    </div>
  );
}
